import type { UserDateFormat } from '@ikegps/core-api';

/**
 * Formats dates according to the user's chosen {@link UserDateFormat}. This is
 * the single source of truth for user-facing date rendering across the browser,
 * the drawing/SVG layer, and server-side exports — the numeric ordering (and the
 * order of the written form) always follows the setting, never the host locale.
 *
 * Two shapes are produced from the same setting:
 *  - short: numeric, e.g. US `06/13/2026`, UK `13/06/2026`
 *  - long:  written, e.g. US `June 13, 2026`, UK `13 June 2026`
 *
 * A stored date field is a calendar day (anchored to UTC midnight), not an
 * instant, so those callers pass `{ timeZone: 'UTC' }` to keep the day stable;
 * real timestamps are rendered in the host's local zone (or a supplied zone).
 */
export type IkeDateFormatOptions = {
	/** IANA zone to render in. Omit for local; pass 'UTC' for calendar-day values. */
	timeZone?: string;
};

export class IkeDateFormat {
	/** Safe fallback when no user setting is available (matches the US default). */
	static readonly DEFAULT: UserDateFormat = 'MM/DD/YYYY';

	/** The two supported formats, for validating untrusted input. */
	static readonly FORMATS: UserDateFormat[] = ['MM/DD/YYYY', 'DD/MM/YYYY'];

	/** True when `value` is one of the supported {@link UserDateFormat} strings. */
	static isFormat(value: string | undefined | null): value is UserDateFormat {
		return value != null && IkeDateFormat.FORMATS.some((format) => format === value);
	}

	/** Narrow an untrusted string (e.g. a request param) to a valid format, or the default. */
	static coerce(value: string | undefined | null): UserDateFormat {
		return IkeDateFormat.isFormat(value) ? value : IkeDateFormat.DEFAULT;
	}

	/** US formats put the month first (`MM/DD/YYYY`); UK puts the day first. */
	static isMonthFirst(format: UserDateFormat): boolean {
		return format.indexOf('MM') < format.indexOf('DD');
	}

	/** Numeric date following the setting exactly, e.g. `06/13/2026` / `13/06/2026`. */
	static short(date: Date, format: UserDateFormat, options?: IkeDateFormatOptions): string {
		const p = IkeDateFormat.parts(date, options?.timeZone, { year: 'numeric', month: '2-digit', day: '2-digit' });
		return format.replace(/YYYY/g, p.year).replace(/MM/g, p.month).replace(/DD/g, p.day);
	}

	/** Written date with full month name, e.g. `June 13, 2026` / `13 June 2026`. */
	static long(date: Date, format: UserDateFormat, options?: IkeDateFormatOptions): string {
		const p = IkeDateFormat.parts(date, options?.timeZone, { year: 'numeric', month: 'long', day: '2-digit' });
		return IkeDateFormat.isMonthFirst(format) ? `${p.month} ${p.day}, ${p.year}` : `${p.day} ${p.month} ${p.year}`;
	}

	/** Short date + 12-hour time, e.g. `06/13/2026 2:30 PM`. */
	static shortDateTime(date: Date, format: UserDateFormat, options?: IkeDateFormatOptions): string {
		return `${IkeDateFormat.short(date, format, options)} ${IkeDateFormat.time(date, options?.timeZone)}`;
	}

	/** Long date + 12-hour time, e.g. `June 13, 2026 2:30 PM`. */
	static longDateTime(date: Date, format: UserDateFormat, options?: IkeDateFormatOptions): string {
		return `${IkeDateFormat.long(date, format, options)} ${IkeDateFormat.time(date, options?.timeZone)}`;
	}

	/**
	 * Parse text typed in the user's format into a UTC-anchored calendar-day Date,
	 * or null when it doesn't match. The Date is anchored to UTC midnight so the
	 * calendar day can never be shifted by the host timezone.
	 */
	static parse(text: string, format: UserDateFormat): Date | null {
		if (text == null || text.trim() === '') {
			return null;
		}

		const tokens = format.split('/');
		const parts = text.trim().split('/');
		if (tokens.length !== parts.length) {
			return null;
		}

		let year: number | null = null;
		let month: number | null = null;
		let day: number | null = null;
		tokens.forEach((token, index) => {
			const part = parts[index].trim();
			if (token === 'YYYY' && /^\d{4}$/.test(part)) {
				year = Number(part);
			} else if (token === 'MM' && /^\d{1,2}$/.test(part)) {
				month = Number(part);
			} else if (token === 'DD' && /^\d{1,2}$/.test(part)) {
				day = Number(part);
			}
		});

		if (year == null || month == null || day == null || month < 1 || month > 12 || day < 1) {
			return null;
		}

		const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
		if (day > daysInMonth) {
			return null;
		}

		return new Date(Date.UTC(year, month - 1, day));
	}

	private static time(date: Date, timeZone?: string): string {
		const p = IkeDateFormat.parts(date, timeZone, { hour: 'numeric', minute: '2-digit', hour12: true });
		return `${p.hour}:${p.minute} ${p.dayPeriod}`;
	}

	// Constructing an Intl.DateTimeFormat is ~18x more expensive than using one
	// (~32µs vs ~1.8µs), so instances are memoised by (timeZone, options). The set of
	// distinct shapes is tiny (short/long/time × a few zones), so this never needs eviction.
	private static formatters: Map<string, Intl.DateTimeFormat> = new Map();

	private static formatter(timeZone: string | undefined, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
		const key = `${timeZone ?? ''}|${JSON.stringify(options)}`;
		let formatter = IkeDateFormat.formatters.get(key);
		if (formatter == null) {
			formatter = new Intl.DateTimeFormat('en-US', timeZone != null ? { ...options, timeZone } : options);
			IkeDateFormat.formatters.set(key, formatter);
		}
		return formatter;
	}

	private static parts(
		date: Date,
		timeZone: string | undefined,
		options: Intl.DateTimeFormatOptions
	): Record<string, string> {
		const result: Record<string, string> = {};
		for (const part of IkeDateFormat.formatter(timeZone, options).formatToParts(date)) {
			result[part.type] = part.value;
		}
		return result;
	}
}
